import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import { pluck, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { API } from '../../shared/constants/app-service-constants';
import * as fromAssetActions from '../store/actions/delivery-assets.actions';
import { DeliveryAsset, TrackTypes } from '../models/delivery-asset.model';
import { SetPlayingAll } from '../store/actions/delivery-assets.actions';
import { HelperService } from './helper.service';

@Injectable({
  providedIn: 'root',
})
export class DeliveryAssetService {
  private assetsTypes = {
    home: (params: any) => this.getHomeAssets(params),
    share: () => this.emptyObservable(),
    audio: (params: any) => this.getMediaAssets(params),
    video: (params: any) => this.getMediaAssets(params),
    documents: (params: any) => this.getAssets(params),
    images: (params: any) => this.getAssets(params),
  };

  constructor(private http: HttpClient, private store: Store<any>) {}

  getSelectedAsset(assets: DeliveryAsset[]): DeliveryAsset {
    return assets.find((asset: DeliveryAsset) => asset.isSelected);
  }

  isLastAsset(assets: DeliveryAsset[], selectedAsset: DeliveryAsset): boolean {
    const nextAsset = this.getNextAsset(assets, selectedAsset);

    return !(nextAsset && nextAsset.campaignTrackId !== undefined);
  }

  isLastVideoAsset(assets: DeliveryAsset[], selectedAsset: DeliveryAsset): boolean {
    const videoAssets = assets
      .filter((asset: DeliveryAsset) => asset.mediaType === 'VIDEO')
      .map((asset: DeliveryAsset) => HelperService.cloneDeliveryAsset(asset));

    return this.isLastAsset(videoAssets, selectedAsset);
  }

  isFirstAsset(assets: DeliveryAsset[], selectedAsset: DeliveryAsset): boolean {
    return !this.getPrevAsset(assets, selectedAsset);
  }

  emptyObservable(): Observable<any> {
    return of([]);
  }

  getHomeAssets(params: { deliveryId: string; category: string }): Observable<DeliveryAsset[]> {
    return this.http
      .get<DeliveryAsset[]>(`${API.DELIVERIES_ASSET}/${params.deliveryId}/asset-category/ALL`)
      .pipe(
        pluck('assets'),
        map((assets: any[]): DeliveryAsset[] => {
          const newAssets = assets.map(
            (asset: any): DeliveryAsset => {
              const { campaignTrack } = asset;
              const newAsset: DeliveryAsset = campaignTrack || asset;

              newAsset.campaignTrackId = newAsset.campaignTrackId
                ? newAsset.campaignTrackId
                : this.getRandomID();
              newAsset.isAllowedToDownload = this.isAllowedToDownload(newAsset);
              return newAsset;
            },
          );

          const mediaAssets = [...this.concatAssetTypes(newAssets)];
          return mediaAssets;
        }),
      );
  }

  getMediaAssets(params: { deliveryId: string; category: string }): Observable<DeliveryAsset[]> {
    return this.http
      .get<DeliveryAsset[]>(
      `${API.DELIVERIES_ASSET}/${params.deliveryId}/asset-category/${params.category}`,
    )
      .pipe(
        pluck('assets'),
        map((assets: any[]): DeliveryAsset[] => {
          const newAssets = assets.map(
            (asset: any): DeliveryAsset => {
              const { campaignTrack, ...assetData } = asset;
              const newAsset: DeliveryAsset = { ...assetData, ...campaignTrack };
              newAsset.category = params.category;
              newAsset.isAllowedToDownload = this.isAllowedToDownload(newAsset);
              return newAsset;
            },
          );

          return this.concatAssetTypes(newAssets);
        }),
      );
  }

  getDeliveryAssets(deliveryId: string, category: string): Observable<DeliveryAsset[]> {
    const params = {
      category,
      deliveryId,
    };
    if (this.assetsTypes[category]) {
      return this.assetsTypes[category](params);
    }
    return this.emptyObservable();
  }

  playNext(
    assets: DeliveryAsset[],
    selectedAsset: DeliveryAsset,
    isPlaying = false,
    audioOnly = false,
  ): void {
    const nextAsset = this.getNextAsset(assets, selectedAsset, audioOnly);

    if (nextAsset && nextAsset.campaignTrackId !== undefined) {
      nextAsset.isPlaying = isPlaying || selectedAsset.isPlaying;
      if (!nextAsset || (nextAsset.mediaType !== 'AUDIO' && nextAsset.mediaType !== 'VIDEO')) {
        this.store.dispatch(SetPlayingAll({ payload: false }));
      }
      this.store.dispatch(fromAssetActions.SetSelectedAsset({ payload: nextAsset }));
    }
  }

  playPrev(assets: DeliveryAsset[], selectedAsset: DeliveryAsset, audioOnly = false): void {
    const prevAsset = this.getPrevAsset(assets, selectedAsset, audioOnly);

    if (prevAsset && prevAsset.campaignTrackId !== undefined) {
      prevAsset.isPlaying = selectedAsset.isPlaying;
      this.store.dispatch(fromAssetActions.SetSelectedAsset({ payload: prevAsset }));
    }
  }

  getAssets(params): Observable<DeliveryAsset[]> {
    const categories = {
      images: 'img',
      documents: 'doc',
      audio: 'audio',
      video: 'video',
    };
    const { deliveryId, category } = params;

    return this.http
      .get<DeliveryAsset[]>(
      `${API.DELIVERIES_ASSET}/${deliveryId}/asset-category/${categories[category]}`,
    )
      .pipe(
        pluck('assets'),
        map((assets: any[]): DeliveryAsset[] => assets.map((asset) => {
          const newAsset = { ...asset };
          newAsset.category = params.category;
          newAsset.campaignTrackId = asset.campaignTrackId
            ? asset.campaignTrackId
            : this.getRandomID();
          newAsset.isAllowedToDownload = this.isAllowedToDownload(newAsset);
          return <DeliveryAsset>newAsset;
        })),
      );
  }

  concatAssetTypes(assets: DeliveryAsset[]): DeliveryAsset[] {
    let ids = assets.map((asset: DeliveryAsset) => asset.campaignTrackId);

    ids = ids.filter((id, i) => id && ids.indexOf(id) === i);
    const newAssets = ids.map((id: string) => {
      const items = assets.filter((asset: any) => asset.campaignTrackId === id);

      // eslint-disable-next-line prefer-spread
      const types = [].concat.apply(
        [],
        items.map((item: any) => item.types),
      );

      if (types && types[0]) {
        items[0].types = types;
      }
      return items[0];
    });

    return newAssets.map((asset: DeliveryAsset) => ({
      ...asset,
      isAllowedToDownload: this.isAllowedToDownload(asset),
    }));
  }

  getFirstStreamAsset(assets: DeliveryAsset[]) {
    const firstAsset = assets.find((asset: DeliveryAsset) => (
      asset && asset.types && asset.types.find((type: TrackTypes) => type.type === 'Streaming')
    ));

    return firstAsset;
  }

  getNextAsset(
    assets: DeliveryAsset[],
    selectedAsset: DeliveryAsset,
    audioOnly = false,
  ): DeliveryAsset {
    let nextAsset: DeliveryAsset;

    if (
      !assets ||
      assets.length <= 1 ||
      !(selectedAsset && selectedAsset.campaignTrackId !== undefined)
    ) {
      return nextAsset;
    }

    // eslint-disable-next-line max-len
    const selectedIndex = assets.findIndex((asset: DeliveryAsset) => asset.campaignTrackId === selectedAsset.campaignTrackId);

    for (let i = selectedIndex + 1; i < assets.length; i += 1) {
      if (
        this.isMatchAudioAsset(assets[i], audioOnly) &&
        (this.isStreamAsset(assets[i]) || this.hasPreviewUrl(assets[i]))
      ) {
        nextAsset = assets[i];
        break;
      }
    }

    return HelperService.cloneDeliveryAsset(nextAsset);
  }

  getPrevAsset(
    assets: DeliveryAsset[],
    selectedAsset: DeliveryAsset,
    audioOnly = false,
  ): DeliveryAsset {
    let prevAsset: DeliveryAsset;

    if (!assets || assets.length <= 1 || !selectedAsset) {
      return prevAsset;
    }

    const selectedIndex = assets.findIndex(
      (asset: DeliveryAsset) => asset.campaignTrackId === selectedAsset.campaignTrackId,
    );

    for (let i = selectedIndex - 1; i >= 0; i -= 1) {
      if (
        this.isMatchAudioAsset(assets[i], audioOnly) &&
        (this.isStreamAsset(assets[i]) || this.hasPreviewUrl(assets[i]))
      ) {
        prevAsset = assets[i];
        break;
      }
    }

    return HelperService.cloneDeliveryAsset(prevAsset);
  }

  isStreamAsset(asset: DeliveryAsset): boolean {
    const link =
      asset.types &&
      asset.types.find((trackType: TrackTypes) => trackType.type.includes('Streaming'));

    return !!link;
  }

  hasPreviewUrl(asset: DeliveryAsset): boolean {
    return !!(asset && asset.previewUrl);
  }

  isDocumentCanBeDownloaded(asset: DeliveryAsset) {
    return asset && !!asset.downloadUrl;
  }

  private isAllowedToDownload(asset: DeliveryAsset): boolean {
    const downloadTypes = {
      audio: this.isMediaCanBeDownloaded,
      video: this.isMediaCanBeDownloaded,
      documents: this.isDocumentCanBeDownloaded,
      images: this.isDocumentCanBeDownloaded,
    };
    let mediaType = asset.mediaType && asset.mediaType.toLowerCase();
    mediaType = mediaType === 'doc' ? 'documents' : mediaType;
    mediaType = mediaType === 'img' ? 'images' : mediaType;
    return downloadTypes[mediaType] && downloadTypes[mediaType](asset.types || asset);
  }

  private isMediaCanBeDownloaded(trackTypes: TrackTypes[]): boolean {
    const typesForDownload = trackTypes.filter(
      (trackType: TrackTypes) => trackType.url && trackType.type !== 'Streaming',
    );
    return typesForDownload.length > 0;
  }

  private getRandomID(): string {
    return String(Math.floor(Math.random() * 10000) + 1);
  }

  private getNotMediaAsset(deliveryAssets: DeliveryAsset[]): DeliveryAsset[] {
    return deliveryAssets.filter((deliveryAsset: DeliveryAsset) => deliveryAsset.mediaType !== 'AUDIO' && deliveryAsset.mediaType !== 'VIDEO');
  }

  private isMatchAudioAsset(asset: DeliveryAsset, audioOnly: boolean): boolean {
    return !audioOnly || (audioOnly && asset.mediaType === 'AUDIO');
  }
}
